"use client";

import { Button, buttonVariants } from "../../elements/button/button";
import type { ImageProps } from "../../elements/image/image";
import { Image } from "../../elements/image/image";
import { TextD2C } from "../../elements/text-d2c/text";
import { IconChevronRight } from "../../icons/chevron-right-icon";
import type { InspectorProps } from "../../lib/inspector";
import { cn } from "../../lib/utils";

export type TeaserAspectRatio = Extract<
  ImageProps["aspectRatio"],
  "1:1" | "2:3"
>;

export type TeaserItemInspectorProps = InspectorProps<
  "container" | "image" | "link"
>;

export type TeaserGridInspectorProps = InspectorProps<"container">;

export type TeaserItem = {
  id: string;
  sys?: { id: string };
  label: string;
  href: string;
  target?: string;
  image: {
    sys?: { id: string };
    src: string;
    srcSet?: string;
    alt: string;
    objectPosition?: ImageProps["objectPosition"];
  };
  inspectorProps?: TeaserItemInspectorProps;
};

export type TeaserGridProps = {
  items: readonly TeaserItem[];
  aspectRatio?: TeaserAspectRatio;
  gap?: "none" | "grid";
  className?: string;
  inspectorProps?: TeaserGridInspectorProps;
  mobileAspectRatio?: TeaserAspectRatio;
  mobileColumnCount?: 1 | 2;
  testId?: string;
};

const MOBILE_GRID_COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
} as const;

const RESPONSIVE_GRID_COLUMNS_BY_COUNT = {
  1: "",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-2 lg:grid-cols-4",
} as const;

const IMAGE_SIZES_BY_COLUMN_COUNT = {
  1: {
    1: "100vw",
    2: "100vw",
  },
  2: {
    1: "(min-width: 768px) 50vw, 100vw",
    2: "50vw",
  },
  3: {
    1: "(min-width: 768px) 34vw, 100vw",
    2: "(min-width: 768px) 34vw, 50vw",
  },
  4: {
    1: "(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw",
    2: "(min-width: 1024px) 25vw, 50vw",
  },
} as const;

const ASPECT_RATIO_CLASSES: Record<TeaserAspectRatio, string> = {
  "1:1": "aspect-square",
  "2:3": "aspect-[2/3]",
};

const RESPONSIVE_ASPECT_RATIO_CLASSES: Record<TeaserAspectRatio, string> = {
  "1:1": "md:aspect-square",
  "2:3": "md:aspect-[2/3]",
};

const MAX_TEASER_GRID_COLUMNS = 4;

function getColumnCount(itemCount: number): 1 | 2 | 3 | 4 {
  return Math.max(1, Math.min(itemCount, MAX_TEASER_GRID_COLUMNS)) as
    | 1
    | 2
    | 3
    | 4;
}

type TeaserTileProps = {
  aspectRatio: TeaserAspectRatio;
  aspectRatioClasses: string;
  imageSizes: string;
  item: TeaserItem;
};

function TeaserTile({
  aspectRatio,
  aspectRatioClasses,
  imageSizes,
  item,
}: TeaserTileProps) {
  return (
    <li className="min-w-0" {...item.inspectorProps?.container}>
      <Button
        aria-label={item.label}
        className={cn(
          "group relative block h-auto w-full overflow-hidden whitespace-normal bg-black text-left text-white focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset",
          aspectRatioClasses
        )}
        data-teaser-id={item.id}
        href={item.href}
        mode="D2C"
        target={item.target}
        unstyled
        {...item.inspectorProps?.link}
      >
        <div
          className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105 group-focus-visible:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
          {...item.inspectorProps?.image}
        >
          <Image
            alt={item.image.alt}
            aspectRatio={aspectRatio}
            fill
            loading="lazy"
            objectFit="cover"
            objectPosition={item.image.objectPosition}
            sizes={imageSizes}
            src={item.image.src}
            srcSet={item.image.srcSet}
          />
        </div>

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
        />

        <div className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-4 p-5 md:p-8">
          <TextD2C color="white" style="teaser-label" tagName="p" variant="h3">
            {item.label}
          </TextD2C>

          <span
            aria-hidden="true"
            className={buttonVariants({ mode: "D2C", size: "icon-lg" })}
          >
            <IconChevronRight aria-hidden="true" />
          </span>
        </div>
      </Button>
    </li>
  );
}

export function TeaserGrid(props: Readonly<TeaserGridProps>) {
  if (props.items.length === 0) {
    return null;
  }

  const aspectRatio = props.aspectRatio ?? "1:1";
  const mobileAspectRatio = props.mobileAspectRatio ?? aspectRatio;
  const columnCount = getColumnCount(props.items.length);
  const mobileColumnCount =
    props.mobileColumnCount === 2 && props.items.length > 1 ? 2 : 1;
  const aspectRatioClasses = cn(
    ASPECT_RATIO_CLASSES[mobileAspectRatio],
    mobileAspectRatio === aspectRatio
      ? undefined
      : RESPONSIVE_ASPECT_RATIO_CLASSES[aspectRatio]
  );
  const imageSizes =
    IMAGE_SIZES_BY_COLUMN_COUNT[columnCount][mobileColumnCount];

  return (
    <ul
      className={cn(
        "m-0 grid w-full list-none p-0",
        props.gap === "none" ? "gap-0" : "gap-unit",
        MOBILE_GRID_COLUMNS[mobileColumnCount],
        RESPONSIVE_GRID_COLUMNS_BY_COUNT[columnCount],
        props.className
      )}
      data-testid={props.testId ?? "teaser-grid"}
      {...props.inspectorProps?.container}
    >
      {props.items.map((item) => (
        <TeaserTile
          aspectRatio={aspectRatio}
          aspectRatioClasses={aspectRatioClasses}
          imageSizes={imageSizes}
          item={item}
          key={item.id}
        />
      ))}
    </ul>
  );
}
